import React from 'react'
import {
  Alert,
  Button,
  Form,
  InputNumber,
  Spin,
  Tabs,
  Typography
} from 'antd'
import { logisticsSettingsApi } from 'api/logisticsSettings'
import { ILogisticsSettings } from 'models/logisticsSettings'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import { DeliveryPolicyForm } from './DeliveryPolicyForm'
import { DeliveryCalculator } from './DeliveryCalculator'
import { OrderPlacesCard } from './OrderPlacesCard'
import styles from './Logistics.module.scss'

const MIN_DAYS = 1
const MAX_DAYS = 90

type TDeliveryTerms = Pick<
  ILogisticsSettings,
  'courier_delivery_days_from' | 'pvz_delivery_days_from'
>

interface IDeliveryTermsFormProps {
  settings: ILogisticsSettings
  onSaved: (settings: ILogisticsSettings) => void
}

const getDeliveryTerms = (settings: ILogisticsSettings): TDeliveryTerms => ({
  courier_delivery_days_from: settings.courier_delivery_days_from,
  pvz_delivery_days_from: settings.pvz_delivery_days_from
})

const DeliveryTermsForm: React.FC<IDeliveryTermsFormProps> = ({
  settings,
  onSaved
}) => {
  const [form] = Form.useForm<TDeliveryTerms>()
  const [isSaving, setIsSaving] = React.useState(false)
  const [isDirty, setIsDirty] = React.useState(false)
  const baseline = React.useRef(getDeliveryTerms(settings))

  React.useEffect(() => {
    const values = getDeliveryTerms(settings)
    baseline.current = values
    form.setFieldsValue(values)
    setIsDirty(false)
  }, [
    form,
    settings.courier_delivery_days_from,
    settings.pvz_delivery_days_from
  ])

  const onSave = async (values: TDeliveryTerms) => {
    try {
      setIsSaving(true)
      const response = await logisticsSettingsApi.updateSettings(values)
      onSaved(response.data)
      showMessage('success', 'Сроки доставки сохранены', '')
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSaving(false)
    }
  }

  const onValuesChange = (_: Partial<TDeliveryTerms>, values: TDeliveryTerms) => {
    setIsDirty(
      values.courier_delivery_days_from !==
        baseline.current.courier_delivery_days_from ||
        values.pvz_delivery_days_from !==
          baseline.current.pvz_delivery_days_from
    )
  }

  return (
    <div className={styles.wrapper}>
      <Typography.Title level={4}>Сроки доставки</Typography.Title>
      <Typography.Text type='secondary' className={styles.description}>
        Покупатель видит эти сроки в карточке товара
      </Typography.Text>
      <Form
        form={form}
        layout='vertical'
        initialValues={baseline.current}
        onFinish={onSave}
        onValuesChange={onValuesChange}
      >
        <Form.Item
          label='Курьером'
          name='courier_delivery_days_from'
          rules={[{ required: true, message: 'Укажите срок доставки' }]}
        >
          <InputNumber
            className={styles.field}
            min={MIN_DAYS}
            max={MAX_DAYS}
            precision={0}
            addonBefore='от'
            addonAfter='дней'
          />
        </Form.Item>
        <Form.Item
          label='Пункты выдачи'
          name='pvz_delivery_days_from'
          rules={[{ required: true, message: 'Укажите срок доставки' }]}
        >
          <InputNumber
            className={styles.field}
            min={MIN_DAYS}
            max={MAX_DAYS}
            precision={0}
            addonBefore='от'
            addonAfter='дней'
          />
        </Form.Item>
        <Button
          type='primary'
          htmlType='submit'
          loading={isSaving}
          disabled={!isDirty}
        >
          Сохранить
        </Button>
      </Form>
    </div>
  )
}

export const ActionsLogistics: React.FC = () => {
  const [settings, setSettings] = React.useState<ILogisticsSettings | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [isLoadError, setIsLoadError] = React.useState(false)

  const getSettings = React.useCallback(async () => {
    try {
      setIsLoading(true)
      setIsLoadError(false)
      const response = await logisticsSettingsApi.getSettings()
      setSettings(response.data)
    } catch (e) {
      setIsLoadError(true)
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }, [])

  React.useEffect(() => {
    getSettings()
  }, [getSettings])

  const onDeliveryTermsSaved = (savedSettings: ILogisticsSettings) => {
    setSettings((currentSettings) =>
      currentSettings
        ? {
          ...currentSettings,
          courier_delivery_days_from:
            savedSettings.courier_delivery_days_from,
          pvz_delivery_days_from: savedSettings.pvz_delivery_days_from,
          updatedAt: savedSettings.updatedAt
        }
        : savedSettings
    )
  }

  const settingsContent = (content: React.ReactNode) => {
    if (isLoadError) {
      return (
        <Alert
          type='error'
          showIcon
          message='Не удалось загрузить настройки логистики'
          description='Проверьте соединение и повторите загрузку. Сохранение настроек пока недоступно.'
          action={<Button onClick={getSettings}>Повторить</Button>}
        />
      )
    }

    return <Spin spinning={isLoading}>{settings ? content : null}</Spin>
  }

  return (
    <div className={styles.root}>
      <Tabs
        items={[
          {
            key: 'overview',
            label: 'Обзор',
            children: (
              <>
                <OrderPlacesCard />
                {settingsContent(
                  settings && (
                    <DeliveryTermsForm
                      settings={settings}
                      onSaved={onDeliveryTermsSaved}
                    />
                  )
                )}
              </>
            )
          },
          {
            key: 'policy',
            label: 'Политика субсидирования',
            children: settingsContent(
              settings && (
                <DeliveryPolicyForm
                  settings={settings}
                  onSaved={setSettings}
                />
              )
            )
          },
          {
            key: 'calculator',
            label: 'Калькулятор доставки',
            children: settingsContent(
              settings && <DeliveryCalculator settings={settings} />
            )
          }
        ]}
      />
    </div>
  )
}
